/**
 * The ranking interaction (DESIGN.md §3.3, deck pages 6 and 7).
 *
 * The board starts complete and alphabetical — all twelve players, ranks 1–12 —
 * and hold-and-drag is the only reordering gesture. There is no drawer, no
 * tap-to-place, no undo and no up/down controls, because with nothing unplaced
 * there is nothing to place: the only job left is to reorder, and one gesture
 * that does exactly that is less to learn than three that overlap.
 *
 * What the deck asks for beat by beat, and where each one lives:
 *
 *   1 DEFAULT  `useRankingModel`, seeded from the alphabetised roster.
 *   2 HOLD     `TouchSensor` with a delay, not a distance — see `sensors`.
 *   3 MOVE     `SortableContext` moves the rows aside — on `SHIFT`, quick and
 *              eased, never a jump — and `DropGap` opens the hole they move
 *              aside for, directly under the card. Rank numbers come from
 *              `newIndex`, so a number belongs to a position on screen, never
 *              to a card.
 *   4 EDGE     `useEdgeAutoScroll`, below: dnd-kit has no pause-before-scroll.
 *   5 DROP     `onDragEnd` → `moveTo` → `onProgress` → the on-device draft.
 *   6 RECOVER  grabbing the same row again; there is no other correction.
 *
 * Accessibility is the one thing here that is invisible (DESIGN §3.3): the row
 * is dnd-kit's own activator, so it is already a focusable `role="button"` that
 * `KeyboardSensor` reorders with the arrow keys, and the announcements below
 * narrate it. None of that is allowed to surface as visible nudge buttons.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  AutoScrollActivator,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
  type ScreenReaderInstructions,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { PlayerId } from '@idgame/protocol';
import { LicenseCard } from '../LicenseCard.js';
import { Button } from '../ui.js';
import { DropGap, slotUnderCard } from './DropGap.js';
import { useRankingModel } from './useRankingModel.js';
import type { RankingCandidate, RankingInteractionProps } from './types.js';

/** The hold that lifts a row. Long enough that a scroll flick is never a drag. */
const HOLD_MS = 180;
/** How close to an edge of the list counts as "at the edge". */
const EDGE_PX = 60;
/** How long you must rest there before the list starts moving under you. */
const EDGE_PAUSE_MS = 450;

/**
 * How the rows that give way move.
 *
 * Quick, because it has to keep up with a thumb, and eased out hard so the row
 * commits immediately and settles rather than drifting. dnd-kit's own default
 * is 200ms `ease`, which reads as slack at this row height.
 *
 * Exactly `DropGap`'s slide, curve and duration both: the hole and the rows
 * that open it are one movement, and two timings would show up as the hole
 * arriving before or after the gap it is drawn in.
 */
const SHIFT: { duration: number; easing: string } = {
  duration: 140,
  easing: 'cubic-bezier(0.2, 0, 0, 1)',
};

/* -------------------------------------------------------------------------- */
/* One row                                                                     */
/* -------------------------------------------------------------------------- */

function Row({
  candidate,
  total,
  disabled,
}: {
  candidate: RankingCandidate;
  total: number;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, newIndex } =
    useSortable({
      id: candidate.id,
      disabled,
      transition: SHIFT,
      attributes: { roleDescription: 'draggable ranking row' },
    });

  /*
    Beat 3: the rank numbers do not change until drop. `newIndex` is the index
    this row is currently translated *to*, so the number a row shows is the
    number of the position it occupies on screen. Every position therefore keeps
    its number for the whole drag while the cards move between them, which is
    the state the deck draws: Leo lifted over "6", Max sitting still at "5".
  */
  const rank = newIndex + 1;

  /*
    The lifted card sits **under the finger**, pixel for pixel.

    It used to ride one pitch below, so that the thumb never covered the hole it
    was aiming at. That reads correctly in a still frame and wrongly in the
    hand: you press a card, and a card one row down comes up. The thing you are
    moving has to be the thing you are touching, and the dashed hole is wide
    enough to read from the sliver either side of the card and from the rows
    that have given way around it.

    `transform.y` is the delta dnd-kit has already restricted to the vertical
    axis, and it is used as-is — the card tracks the hand with no offset and no
    snapping, which is what makes a correction land on the frame it was made.
  */
  const style = {
    transform: transform
      ? `translate3d(0, ${transform.y}px, 0) scale(${isDragging ? 1.02 : 1})`
      : undefined,
    // Nothing eases the card itself: a transition on the thing under the finger
    // is lag by another name. The rows it displaces get `SHIFT`.
    transition: isDragging ? 'none' : transition,
    // Promotes the row to its own layer for the duration, so a twelve-row board
    // repaints only what moved.
    willChange: transform ? ('transform' as const) : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`rank-row-${candidate.id}`}
      data-rank={rank}
      data-dragging={isDragging ? 'true' : 'false'}
      aria-label={`${candidate.name}, rank ${rank} of ${total}`}
      className="relative list-none touch-manipulation select-none"
      {...attributes}
      {...listeners}
    >
      {/*
        The lift is a drag state, and `lift` is the one colour in the palette
        with no home on a resting row — so it is stamped over LicenseCard's
        cream rather than added there as a variant nothing else would ever use.
      */}
      <LicenseCard
        player={candidate}
        variant="chip"
        rank={rank}
        className={
          isDragging
            ? 'border-2! border-terracotta! bg-lift! shadow-[0_10px_24px_rgba(38,49,39,0.22)]'
            : ''
        }
      >
        <span
          aria-hidden="true"
          className="shrink-0 text-[0.625rem] font-semibold tracking-[0.14em] text-ink-soft uppercase"
        >
          ≡
        </span>
      </LicenseCard>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Beat 4 — pause at the edge                                                  */
/* -------------------------------------------------------------------------- */

/**
 * dnd-kit auto-scrolls the moment the dragged rect enters the threshold band,
 * which on a list this dense means the board bolts as soon as you reach for the
 * top. The deck asks for a *pause*: rest within ~60px of an edge for 450ms and
 * only then does the list start to move, slowly at first.
 *
 * So the threshold is sized to 60px of the actual list and the scroller is held
 * disabled until the timer fires. Leaving the band cancels it, which is what
 * makes it a pause rather than a delay — sweeping past the top on the way
 * somewhere else never starts a scroll.
 */
function useEdgeAutoScroll(listRef: RefObject<HTMLElement | null>) {
  const [armed, setArmed] = useState(false);
  const [threshold, setThreshold] = useState(0.15);
  const timer = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const begin = useCallback(() => {
    const height = listRef.current?.clientHeight ?? 0;
    setThreshold(height > 0 ? Math.min(0.4, EDGE_PX / height) : 0.15);
    setArmed(false);
    clear();
  }, [clear, listRef]);

  const track = useCallback(
    (rect: { top: number; bottom: number } | null) => {
      const list = listRef.current?.getBoundingClientRect();
      if (!rect || !list) return;
      const nearEdge = rect.top < list.top + EDGE_PX || rect.bottom > list.bottom - EDGE_PX;
      if (!nearEdge) {
        clear();
        setArmed(false);
        return;
      }
      if (armed || timer.current !== null) return;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setArmed(true);
      }, EDGE_PAUSE_MS);
    },
    [armed, clear, listRef],
  );

  const end = useCallback(() => {
    clear();
    setArmed(false);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return {
    begin,
    track,
    end,
    options: {
      enabled: armed,
      activator: AutoScrollActivator.DraggableRect,
      // Gentler than dnd-kit's default of 10: the speed still ramps with how
      // far into the band you push, so it starts slow and accelerates.
      acceleration: 8,
      threshold: { x: 0, y: threshold },
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Beat 3 — where the hole goes                                                */
/* -------------------------------------------------------------------------- */

/**
 * Every row sits on the same pitch, so one measurement taken as the drag starts
 * places the gap for the whole of it: slot *n* begins `top + n * pitch` down the
 * list's content box.
 *
 * Measuring once, and drawing the gap out of flow, is the point. A gap that
 * took part in layout would move every row under it the moment it appeared,
 * while dnd-kit is still collision-testing against the rects it measured at
 * pick-up — the board would fight the finger. Out of flow, the hole is exactly
 * as wide and as tall as the row that will fill it, and nothing else moves.
 */
function useSlotGeometry(listRef: RefObject<HTMLElement | null>) {
  const geometry = useRef({ top: 0, pitch: 0, height: 0 });

  const measure = useCallback(() => {
    const rows = listRef.current?.querySelectorAll<HTMLElement>('[data-rank]');
    const first = rows?.[0];
    const second = rows?.[1];
    if (!first || !second) return;
    geometry.current = {
      top: first.offsetTop,
      pitch: second.offsetTop - first.offsetTop,
      height: first.offsetHeight,
    };
  }, [listRef]);

  return { measure, geometry };
}

/* -------------------------------------------------------------------------- */
/* The interaction                                                             */
/* -------------------------------------------------------------------------- */

export function HoldDragRanking({
  candidates,
  initialRanking,
  disabled = false,
  onProgress,
  onDraggingChange,
  submitNotice,
  onSubmit,
}: RankingInteractionProps) {
  // "Starts A–Z" is a property of the interaction, not of whatever order the
  // screen hands over, so the sort lives here. The id breaks ties, so two Sams
  // cannot swap places between renders.
  const byId = useMemo(() => new Map(candidates.map((c) => [c.id, c])), [candidates]);
  const alphabetical = useMemo(
    () =>
      [...candidates]
        .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
        .map((c) => c.id),
    [candidates],
  );

  const ranking = useRankingModel(alphabetical, initialRanking);

  /*
    Referentially stable, and that is the whole point of the memo.

    `SortableContext` compares `items` by identity, and `useSortable` kills its
    own transition for one frame whenever the array has changed — a guard
    against rows animating back to a position the list no longer has. A fresh
    `[...ranking.ranking]` on every render made "changed" permanently true, so
    every row that returned to its home lane got `transform 0ms linear` and
    snapped there. That is the jump: the rows moving *away* eased, the rows
    coming back did not, and the board flickered under the finger.
  */
  const items = useMemo(() => [...ranking.ranking], [ranking.ranking]);
  const [dragging, setDragging] = useState<PlayerId | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  const edge = useEdgeAutoScroll(listRef);
  const slot = useSlotGeometry(listRef);
  const aim = useRef<number | null>(null);
  const collisionDetection = useMemo(
    () => slotUnderCard(ranking.ranking, aim),
    [ranking.ranking],
  );

  // Held in refs so an inline callback prop cannot start a render loop.
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  useEffect(() => {
    onProgressRef.current?.(ranking.ranking);
  }, [ranking.ranking]);

  const onDraggingChangeRef = useRef(onDraggingChange);
  onDraggingChangeRef.current = onDraggingChange;
  useEffect(() => {
    onDraggingChangeRef.current?.(dragging ? (byId.get(dragging)?.name ?? null) : null);
  }, [dragging, byId]);

  const sensors = useSensors(
    /*
      Touch activates on a HOLD, never on a distance. A distance constraint
      needs `touch-action: none` on every row, which would kill vertical
      scrolling of a twelve-row list — the exact list this exists for. The hold
      is also the deck's beat 2: it is the gesture, not a compromise.
    */
    useSensor(TouchSensor, { activationConstraint: { delay: HOLD_MS, tolerance: 8 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const nameOf = useCallback(
    (id: unknown) => byId.get(String(id))?.name ?? 'that player',
    [byId],
  );
  const placeOf = useCallback(
    (id: unknown) => (ranking.indexOf(String(id)) ?? 0) + 1,
    [ranking],
  );

  const announcements = useMemo<Announcements>(
    () => ({
      onDragStart: ({ active }) =>
        `Picked up ${nameOf(active.id)}, position ${placeOf(active.id)} of ${ranking.total}.`,
      onDragOver: ({ active, over }) =>
        over ? `${nameOf(active.id)} is over position ${placeOf(over.id)}.` : undefined,
      onDragEnd: ({ active, over }) =>
        over
          ? `${nameOf(active.id)} dropped at position ${placeOf(over.id)} of ${ranking.total}.`
          : `${nameOf(active.id)} returned to where it was.`,
      onDragCancel: ({ active }) =>
        `Cancelled. ${nameOf(active.id)} returned to where it was.`,
    }),
    [nameOf, placeOf, ranking.total],
  );

  function handleDragStart(event: DragStartEvent) {
    setDragging(String(event.active.id));
    slot.measure();
    aim.current = null;
    setDropIndex(ranking.indexOf(String(event.active.id)));
    edge.begin();
  }

  function handleDragMove(event: DragMoveEvent) {
    edge.track(event.active.rect.current.translated);
  }

  // `over` is the row the lifted one would displace, which is the same index
  // `handleDragEnd` drops into — so the hole is always where release lands.
  function handleDragOver({ over }: DragOverEvent) {
    if (over) setDropIndex(ranking.indexOf(String(over.id)));
  }

  function letGo() {
    setDragging(null);
    setDropIndex(null);
    aim.current = null;
    edge.end();
  }

  function handleDragEnd(event: DragEndEvent) {
    letGo();
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const to = ranking.indexOf(String(over.id));
    if (to !== null) ranking.moveTo(String(active.id), to);
  }

  const rows = ranking.ranking
    .map((id) => byId.get(id))
    .filter((c): c is RankingCandidate => c !== undefined);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[restrictToVerticalAxis]}
      autoScroll={edge.options}
      accessibility={{ announcements, screenReaderInstructions: INSTRUCTIONS }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={letGo}
    >
      <div className="flex min-h-0 flex-1 flex-col" data-testid="ranking-interaction">
        {/* The deck allows exactly one helper line, and it changes once. */}
        <p
          className="shrink-0 pb-2.5 text-[0.75rem] font-semibold text-green"
          data-testid="rank-hint"
        >
          {dragging ? 'Hold. Move. Release.' : 'Starts A–Z. Hold and drag to rank.'}
        </p>

        {/*
          The list takes whatever the column has left and scrolls inside it, so
          Submit — the one control the player is reaching for — never leaves the
          bottom of the phone. At 390×844 twelve 46px rows land exactly inside;
          on anything shorter the list gives up the difference, not the button.
        */}
        {/*
          No extra bottom padding during a drag. The card used to be drawn one
          lane below the finger, which borrowed a lane the last row had to be
          given somewhere to go into; under the finger it occupies the same lane
          it always did, and the list keeps its resting height.
        */}
        <ol
          ref={listRef}
          data-testid="rank-list"
          className="relative min-h-0 flex-1 space-y-1 overflow-y-auto pb-2"
        >
          {/* First in the list, so a row animating aside passes over the hole
              rather than under it. */}
          {dropIndex !== null && slot.geometry.current.height > 0 && (
            <DropGap
              rank={dropIndex + 1}
              top={slot.geometry.current.top + dropIndex * slot.geometry.current.pitch}
              height={slot.geometry.current.height}
            />
          )}
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {rows.map((candidate) => (
              <Row
                key={candidate.id}
                candidate={candidate}
                total={ranking.total}
                disabled={disabled}
              />
            ))}
          </SortableContext>
        </ol>

        {/*
          Always enabled: the list is complete from the first frame, so there is
          nothing to gate on. `disabled` here is only the in-flight submission.
        */}
        <div className="shrink-0 pt-1.5">
          {submitNotice}
          <Button
            disabled={disabled}
            data-testid="rank-submit"
            onClick={() => onSubmit([...ranking.ranking])}
          >
            Submit
          </Button>
        </div>
      </div>
    </DndContext>
  );
}

const INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    'Press space or enter to pick up a player. Use the up and down arrow keys to move them ' +
    'through the ranking. Press space or enter again to drop them, or escape to cancel.',
};
